<template>
  <Tabbar v-model="active" :safe-area-inset-bottom="true" active-color="#aa341b" style="z-index: 20;" @change="changeTab">
    <TabbarItem v-if="showTeamReport" name="manager-overview">
      <span>{{ $t('manager_overview_tab_overview') }}</span>
      <template #icon="{ active }">
        <van-icon :name="active ? 'wap-home' : 'wap-home-o'" />
      </template>
    </TabbarItem>
    <TabbarItem v-if="showTeamReport" name="team">
      <span>{{ $t('tab_team') }}</span>
      <template #icon="{ active }">
        <van-icon :name="active ? 'manager' : 'manager-o'" />
      </template>
    </TabbarItem>
    <TabbarItem v-if="showAIAssistant && !isSalesManagerRole" name="home">
      <span>{{ $t('home') }}</span>
      <template #icon="{ active }">
        <van-icon :name="active ? 'wap-home' : 'wap-home-o'" />
      </template>
    </TabbarItem>
    <TabbarItem name="hcp">
      <span>{{ $t('tab_customer') }}</span>
      <template #icon="{ active }">
        <van-icon :name="active ? 'friends' : 'friends-o'" />
      </template>
    </TabbarItem>
    <TabbarItem v-if="!showTeamReport" name="visit" :badge="visitDraftCount || ''">
      <span>{{ $t('tab_visit') }}</span>
      <template #icon="{ active }">
        <van-icon :name="active ? 'chat' : 'chat-o'" />
      </template>
    </TabbarItem>
    <TabbarItem v-if="showAIAssistant" name="ai" class="ai-tab-item">
      <template #icon="{ active }">
        <div class="ai-icon-wrapper">
          <font-awesome-icon :icon="[active ? 'fas' : 'far', 'lightbulb']" class="ai-icon" />
        </div>
      </template>
      <span>{{ $t('ai_assistant') }}</span>
    </TabbarItem>
    <TabbarItem v-if="showTeamReport" name="visit" :badge="visitDraftCount || ''">
      <span>{{ $t('tab_visit') }}</span>
      <template #icon="{ active }">
        <van-icon :name="active ? 'chat' : 'chat-o'" />
      </template>
    </TabbarItem>
    <TabbarItem v-if="!showTeamReport && !showTaskManagement" name="task" :badge="taskCount || ''">
      <span>{{ $t('tab_task') }}</span>
      <template #icon="{ active }">
        <van-icon :name="active ? 'clock' : 'clock-o'" />
      </template>
    </TabbarItem>
    <TabbarItem v-if="!showTeamReport && showTaskManagement" name="task-management">
      <span>{{ $t('tab_task') }}</span>
      <template #icon="{ active }">
        <van-icon :name="active ? 'todo-list' : 'todo-list-o'" />
      </template>
    </TabbarItem>
    <TabbarItem name="live">
      <span>{{ $t('tab_live') }}</span>
      <template #icon="{ active }">
        <van-icon :name="active ? 'video' : 'video-o'" />
      </template>
    </TabbarItem>
    <TabbarItem name="content">
      <span>{{ $t('tab_content') }}</span>
      <template #icon="{ active }">
        <van-icon :name="active ? 'newspaper' : 'newspaper-o'" />
      </template>
    </TabbarItem>
  </Tabbar>
</template>

<script>
import {Tabbar, TabbarItem, Icon} from 'vant';
import {getLocalStorage, isEnableAI, isSalesManagerTerritory, isSpecialSalesOverviewRole} from '@/utils/index';
import { getMyCampaignEventStat } from '@/api/nba';
import {listVisitDrafts} from '@/utils/visitDraft';

export default {
  name: 'TabBar',
  components: {
    Tabbar,
    TabbarItem,
    vanIcon: Icon,
  },
  data() {
    return {
      active: 'hcp',
      taskCount: 0,
      visitDraftCount: 0,
      isSalesManagerRole: false,
      showTaskManagement: false,
      showTeamReport: false,
      showAIAssistant: isEnableAI(),
    };
  },
  async created() {
    // 判断是否显示 task-management
    const roleName = localStorage.getItem('role_name');
    const roleId = localStorage.getItem('role_id');
    const territoryType = localStorage.getItem('territory_type');
    const adminRoles = ['管理员', '市场部', 'RPM'];
    const adminTerritories = ['DM', 'AM', 'RPM'];
    const managerTerritories = ['DM', 'AM', 'SD'];
    const isSpecialSalesOverview = isSpecialSalesOverviewRole(roleId, roleName);

    this.showAIAssistant = isEnableAI();
    this.isSalesManagerRole = isSalesManagerTerritory(territoryType) || isSpecialSalesOverview;

    this.showTaskManagement =
      !isSpecialSalesOverview && (adminRoles.includes(roleName) || adminTerritories.includes(territoryType));
    this.showTeamReport = managerTerritories.includes(territoryType) || isSpecialSalesOverview;

    // 初始化时根据路由设置 active 值
    this.updateActiveTab(this.$route.name);

    // 监听路由变化
    this.$router.afterEach((to) => {
      this.updateActiveTab(to.name);
    });

    if (!this.showTeamReport && !this.showTaskManagement && this.$route.name !== 'overview') {
      this.getTaskCount();
    }
    this.getVisitDraftCount();

    this.$root.$on('updateTaskCount', this.handleTaskCountUpdate);
    this.$root.$on('updateVisitDraftCount', this.getVisitDraftCount);
  },
  beforeDestroy() {
    this.$root.$off('updateTaskCount', this.handleTaskCountUpdate);
    this.$root.$off('updateVisitDraftCount', this.getVisitDraftCount);
  },
  methods: {
    handleTaskCountUpdate(count) {
      if (typeof count === 'number') {
        this.taskCount = count;
        return;
      }

      this.getTaskCount();
    },
    async getTaskCount() {
      try {
        const res = await getMyCampaignEventStat();
        this.taskCount = res && res.data ? res.data.unexecuted_task_count || 0 : 0;
      } catch (error) {
        console.error('Failed to get task count:', error);
        this.taskCount = 0;
      }
    },
    getVisitDraftCount() {
      this.visitDraftCount = listVisitDrafts({
        userId: getLocalStorage('user_id'),
        mode: 'hcp',
      }).length;
    },
    updateActiveTab(routeName) {
      console.log(routeName);
      switch (routeName) {
        case 'overview':
          this.active = this.isSalesManagerRole ? 'manager-overview' : 'home';
          break;
        case 'doctorListHome':
        case 'listHome':
        case 'list':
        case 'hcp':
          this.active = 'hcp';
          break;
        case 'visitListHome':
          this.active = 'visit';
          break;
        case 'Live':
          this.active = 'live';
          break;
        case 'contentHome':
          this.active = 'content';
          break;
        case 'nbaListHome':
          this.active = 'nba';
          break;
        case 'reportHome':
          this.active = 'report';
          break;
        case 'teamHome':
          this.active = 'team';
          break;
        case 'task':
        case 'taskSummaryDetail':
          this.active = 'task';
          break;
        case 'task-management':
          this.active = 'task-management';
          break;
        default:
          this.active = this.showTeamReport ? 'manager-overview' : 'hcp';
          break;
      }
    },
    changeTab(e) {
      switch (e) {
        case 'manager-overview':
          this.$router.push({
            path: '/hcp/overview',
          });
          break;
        case 'home':
          this.$router.push({
            path: '/hcp/overview',
          });
          break;
        case 'hcp':
          this.$router.push({
            path: '/hcp/list',
            query: {
              user_id: getLocalStorage('user_id_key'),
            },
          });
          break;
        case 'ai':
          this.$root.$emit('open-ai-chat');
          this.$nextTick(() => {
            this.updateActiveTab(this.$route.name);
          });
          break;
        case 'visit':
          this.$router.push({
            path: '/hcp/visit-list',
          });
          break;
        case 'live':
          this.$router.push({
            path: '/hcp/live',
          });
          break;
        case 'content':
          this.$router.push({
            path: '/hcp/content',
          });
          break;
        case 'nba':
          this.$router.push({
            path: '/hcp/nba-list',
          });
          break;
        case 'report':
          this.$router.push({
            path: '/hcp/report',
          });
          break;
        case 'team':
          this.$router.push({
            path: '/hcp/team',
          });
          break;
        case 'task':
          this.$router.push({
            path: '/hcp/task',
          });
          break;
        case 'task-management':
          this.$router.push({
            path: '/hcp/task-management',
          });
          break;
        default:
          break;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
::v-deep {
  .van-tabbar__item--active {
    color: #aa341b;
  }
  .van-info {
    background-color: #aa341b;
    font-size: 9px;
  }

  .van-tabbar-item__text {
    font-size: 10px;
  }

  .ai-tab-item {
    .van-tabbar-item__text {
      font-size: 9px;
      transform-origin: center top;
      margin-top: -14px;
      display: inline-block;
    }

    .van-tabbar-item__icon {
      margin-bottom: 0;
      height: 48px;
    }

    .ai-icon-wrapper {
      position: absolute;
      top: -15px;
      left: 50%;
      transform: translateX(-50%);
      width: 40px;
      height: 40px;
      border-radius: 50%;
      background: linear-gradient(135deg, #4d50e9, #b84fd7, #aa341b);
      display: flex;
      justify-content: center;
      align-items: center;
      box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.1);
      border: 4px solid #fff;
      z-index: 10;

      .ai-icon {
        color: white;
        font-size: 20px;
      }
    }
  }
}
</style>
